import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "mc_cookie_consent_v1";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [analyticsAccepted, setAnalyticsAccepted] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) {
        // Small delay to let the page settle first
        const timer = setTimeout(() => setVisible(true), 800);
        return () => clearTimeout(timer);
      }
    } catch (e) {
      setVisible(true);
    }
  }, []);

  const save = (consent) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ ...consent, timestamp: new Date().toISOString() })
      );
    } catch (e) {
      /* localStorage non disponibile, ignoriamo */
    }
    setVisible(false);
    setShowCustomize(false);
  };

  const acceptAll = () => save({ necessary: true, analytics: true });
  const acceptOnlyNecessary = () => save({ necessary: true, analytics: false });
  const saveCustom = () => save({ necessary: true, analytics: analyticsAccepted });

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed bottom-0 left-0 right-0 z-[60] p-4 md:p-6 animate-[slideUp_0.4s_ease-out]"
      style={{ animation: "slideUp 0.4s ease-out" }}
    >
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
      <div className="max-w-[1400px] mx-auto bg-white border border-[#E5E5E0] shadow-2xl">
        {!showCustomize ? (
          <div className="p-6 md:p-8 grid md:grid-cols-[1fr_auto] gap-6 items-start">
            <div className="flex gap-4">
              <div className="hidden md:flex h-11 w-11 border border-[#1B3022] text-[#1B3022] items-center justify-center flex-shrink-0">
                <Cookie className="h-5 w-5" />
              </div>
              <div>
                <div className="overline text-[#1B3022]" data-testid="cookie-banner-title">Informativa cookie</div>
                <p className="text-[#111] mt-2 text-[14px] leading-relaxed max-w-3xl">
                  Questo sito utilizza cookie tecnici necessari al funzionamento. Con il tuo consenso,
                  potremmo utilizzare anche cookie di analisi per migliorare l'esperienza utente.
                  Per maggiori informazioni consulta la nostra{" "}
                  <Link to="/cookie-policy" className="underline text-[#1B3022] hover:text-[#111]" data-testid="cookie-banner-policy-link">
                    Cookie Policy
                  </Link>{" "}
                  e la{" "}
                  <Link to="/privacy-policy" className="underline text-[#1B3022] hover:text-[#111]" data-testid="cookie-banner-privacy-link">
                    Privacy Policy
                  </Link>.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 md:flex-col md:gap-2 md:min-w-[200px]">
              <button
                onClick={acceptAll}
                data-testid="cookie-accept-all"
                className="flex-1 md:flex-none bg-[#1B3022] text-white px-5 py-3 text-sm hover:bg-[#2C4A36] transition-colors"
              >
                Accetta tutti
              </button>
              <button
                onClick={acceptOnlyNecessary}
                data-testid="cookie-only-necessary"
                className="flex-1 md:flex-none border border-[#111] text-[#111] px-5 py-3 text-sm hover:bg-[#111] hover:text-white transition-colors"
              >
                Solo necessari
              </button>
              <button
                onClick={() => setShowCustomize(true)}
                data-testid="cookie-customize"
                className="flex-1 md:flex-none text-[#555] px-5 py-3 text-sm hover:text-[#111] transition-colors underline underline-offset-4"
              >
                Personalizza
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 md:p-8" data-testid="cookie-customize-panel">
            <div className="flex items-start justify-between">
              <div>
                <div className="overline text-[#1B3022]">Preferenze cookie</div>
                <h3 className="font-serif-display text-2xl mt-2 text-[#111]">Personalizza le tue preferenze</h3>
              </div>
              <button
                onClick={() => setShowCustomize(false)}
                className="p-2 text-[#555] hover:text-[#111]"
                data-testid="cookie-customize-close"
                aria-label="Chiudi"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-6 space-y-4 max-w-3xl">
              <div className="border border-[#E5E5E0] p-5 bg-[#FBFBF9]">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-medium text-[#111]">Cookie tecnici (necessari)</div>
                    <p className="text-sm text-[#555] mt-1 leading-relaxed">
                      Indispensabili per il corretto funzionamento del sito. Non possono essere disattivati.
                    </p>
                  </div>
                  <div className="text-xs uppercase tracking-wider text-[#1B3022] font-medium whitespace-nowrap">Sempre attivi</div>
                </div>
              </div>
              <div className="border border-[#E5E5E0] p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-medium text-[#111]">Cookie analitici</div>
                    <p className="text-sm text-[#555] mt-1 leading-relaxed">
                      Ci aiutano a capire come gli utenti utilizzano il sito (in forma anonima e aggregata)
                      per migliorarlo. Attualmente non sono in uso.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={analyticsAccepted}
                      onChange={(e) => setAnalyticsAccepted(e.target.checked)}
                      className="sr-only peer"
                      data-testid="cookie-toggle-analytics"
                    />
                    <div className="w-11 h-6 bg-[#E5E5E0] peer-checked:bg-[#1B3022] rounded-full transition-colors relative">
                      <div className={`absolute top-0.5 left-0.5 bg-white w-5 h-5 rounded-full transition-transform ${analyticsAccepted ? 'translate-x-5' : ''}`} />
                    </div>
                  </label>
                </div>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                onClick={saveCustom}
                data-testid="cookie-save-preferences"
                className="bg-[#1B3022] text-white px-6 py-3 text-sm hover:bg-[#2C4A36] transition-colors"
              >
                Salva preferenze
              </button>
              <button
                onClick={acceptAll}
                data-testid="cookie-customize-accept-all"
                className="border border-[#111] text-[#111] px-6 py-3 text-sm hover:bg-[#111] hover:text-white transition-colors"
              >
                Accetta tutti
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
